import { type Edge, type Node, useUpdateNodeInternals } from '@xyflow/react'
import { useQueuesQuery, useTeamsQuery } from 'v2/pages/settings/queries'
import { FlowSourceTypes } from 'v2/types'
import { vibeAgentLogger as logger } from 'v2/utils/logger'
import { DEFAULT_QUEUE_NAME, DEFAULT_TEAM_NAME } from '../../constants'
import { useTypedReactFlow } from '../../hooks/use-react-flow'
import { useFlowStore } from '../../store'
import { FlowNodeTypes, type GenericEdge, type GenericNode, type NodeDataBase } from '../../types'
import { getPhloTypeFromStartNodeTrigger, refreshNodeVars } from '../../utils'

// Types for better type safety
interface WorkflowData {
  nodes: GenericNode[]
  edges: GenericEdge[]
}

interface ElementsToDelete {
  nodes: GenericNode[]
  edges: GenericEdge[]
}

interface WorkflowUpdateResult {
  nodesToAdd: GenericNode[]
  nodesToUpdate: GenericNode[]
}

export const useWorkflowUpdater = () => {
  // FE-side defaults enrichment sources
  const { data: queues } = useQueuesQuery()
  const { data: teamsData } = useTeamsQuery()

  const { getNodes, deleteElements, updateEdgeData, updateNodeData, getEdges, setNodes, setEdges } =
    useTypedReactFlow()
  const updateNodeInternals = useUpdateNodeInternals()

  /**
   * Handles flow type changes when start node is added or updated
   * Updates both the global flow type and edge source handles
   */
  const handleStartNodeFlowTypeChange = (startNode: Node) => {
    const flowType = getPhloTypeFromStartNodeTrigger(startNode)
    if (!flowType) return

    // Update global flow type
    const { setCurrentFlowType } = useFlowStore.getState()
    setCurrentFlowType(flowType)

    // Update edge source handles to match new trigger type
    const sourceHandle = FlowSourceTypes[flowType]
    const edges = getEdges()
    const startNodeEdge = edges.find((edge) => edge.source === startNode.id)

    if (startNodeEdge) {
      setEdges(
        edges.map((edge) => (edge.id === startNodeEdge.id ? { ...edge, sourceHandle } : edge)),
      )
      updateNodeInternals(startNode.id)
    }
  }

  /**
   * Validates and sanitizes workflow data by ensuring valid positions and removing duplicates
   */
  const validateAndSanitizeWorkflowData = (workflowData: WorkflowData): WorkflowData => {
    // Create a copy to avoid mutating the original parameter
    const sanitizedData = { ...workflowData }

    // Guard-rail: ensure every node has a valid position so React-Flow can render it
    if (Array.isArray(sanitizedData.nodes)) {
      let fallbackIdx = 0
      sanitizedData.nodes = sanitizedData.nodes.map((n: GenericNode) => {
        if (!n.position || typeof n.position.x !== 'number' || typeof n.position.y !== 'number') {
          const pos = { x: 0, y: fallbackIdx * 150 }
          fallbackIdx += 1
          return { ...n, position: pos }
        }
        return n
      })
    }

    // Deduplicate the backend payload before processing
    const nodeIds = sanitizedData.nodes.map((n: GenericNode) => n.id)
    const duplicateNodeIds = nodeIds.filter(
      (id: string, index: number) => nodeIds.indexOf(id) !== index,
    )
    const edgeIds = sanitizedData.edges.map((e: GenericEdge) => e.id)
    const duplicateEdgeIds = edgeIds.filter(
      (id: string, index: number) => edgeIds.indexOf(id) !== index,
    )

    if (duplicateNodeIds.length > 0 || duplicateEdgeIds.length > 0) {
      sanitizedData.nodes = Array.from(
        new Map(sanitizedData.nodes.map((n: GenericNode) => [n.id, n])).values(),
      )
      sanitizedData.edges = Array.from(
        new Map(sanitizedData.edges.map((e: GenericEdge) => [e.id, e])).values(),
      )
    }

    return sanitizedData
  }

  const enrichWithDefaults = (workflowData: WorkflowData): WorkflowData => {
    const enrichedNodes = workflowData.nodes.map((n) => {
      /**
       * Enrich queue_and_route nodes with FE-known defaults if missing.
       * - queue: set to "Simple Queue" uuid when empty
       * - routing_teams: set to ["default_team" id] when empty
       */
      if (n.type === FlowNodeTypes.queue_and_route) {
        // Get the default queue and team
        // If no default queue or team is found, use the first queue and team
        const defaultQueueUuid =
          queues?.find((q) => q.name === DEFAULT_QUEUE_NAME)?.uuid || queues?.[0]?.uuid
        const defaultTeamId =
          teamsData?.teams?.find((t) => t.name === DEFAULT_TEAM_NAME)?.id ||
          teamsData?.teams?.[0]?.id

        const existingData = n.data as NodeDataBase<unknown>
        const nodeCopy: GenericNode = {
          ...n,
          data: {
            ...existingData,
            config: {
              ...(existingData?.config || {}),
            },
          },
        }
        type QNRConfig = { queue?: string; routing_teams?: string[] } & Record<string, unknown>
        const cfg = (nodeCopy.data as NodeDataBase<unknown>).config as unknown as QNRConfig

        if (defaultQueueUuid && (!cfg.queue || cfg.queue === '')) {
          cfg.queue = defaultQueueUuid
        }
        const teams = Array.isArray(cfg.routing_teams) ? (cfg.routing_teams as string[]) : []
        if (defaultTeamId && teams.length === 0) {
          cfg.routing_teams = [defaultTeamId]
        }
        return nodeCopy
      }
      // for other nodes, return the node as is
      return n
    })

    return { ...workflowData, nodes: enrichedNodes }
  }

  /**
   * Identifies elements that need to be deleted based on AI start node and current state
   */
  const identifyElementsToDelete = (
    workflowData: WorkflowData,
    currentNodes: GenericNode[],
    currentEdges: GenericEdge[],
  ): ElementsToDelete => {
    const elementsToDelete: ElementsToDelete = { nodes: [], edges: [] }

    const aiStartNode = workflowData.nodes.find((node: Node) => node.type === FlowNodeTypes.start)
    const currentSelectTrigger = currentNodes.find(
      (node) => node.type === FlowNodeTypes.select_trigger,
    )

    if (aiStartNode) {
      if (currentSelectTrigger) {
        logger.debug('Marking select trigger for removal due to AI start node')
        elementsToDelete.nodes.push(currentSelectTrigger)
        elementsToDelete.edges.push(
          ...currentEdges.filter(
            (edge) =>
              edge.source === currentSelectTrigger.id || edge.target === currentSelectTrigger.id,
          ),
        )
      }
    }

    return elementsToDelete
  }

  /**
   * Performs the actual workflow updates by adding and updating nodes and edges
   */
  const performWorkflowUpdates = (
    workflowData: WorkflowData,
    currentNodes: GenericNode[],
    currentEdges: GenericEdge[],
  ): WorkflowUpdateResult => {
    // 1. Identify nodes to add
    const nodesToAdd = workflowData.nodes.filter(
      (newNode: Node) => !currentNodes.find((existingNode) => existingNode.id === newNode.id),
    )

    // 2. Identify edges to add
    const edgesToAdd = workflowData.edges.filter(
      (newEdge: Edge) => !currentEdges.find((existingEdge) => existingEdge.id === newEdge.id),
    )

    // 3. Identify nodes to update
    const nodesToUpdate = workflowData.nodes.filter((newNode: Node) => {
      const existingNode = currentNodes.find((existing) => existing.id === newNode.id)
      return existingNode && JSON.stringify(existingNode.data) !== JSON.stringify(newNode.data)
    })

    // 4. Identify edges to update
    const edgesToUpdate = workflowData.edges.filter((newEdge: Edge) => {
      const existingEdge = currentEdges.find((existing) => existing.id === newEdge.id)
      return existingEdge && JSON.stringify(existingEdge.data) !== JSON.stringify(newEdge.data)
    })

    // Note: Node and edge deletions are handled separately via the delete-safe
    // snapshot reconcile in canvas-sync, not here.

    // Apply node additions
    if (nodesToAdd.length > 0) {
      setNodes((nodes: Node<NodeDataBase<unknown>>[]) => [
        ...nodes,
        ...(nodesToAdd as Node<NodeDataBase<unknown>>[]).filter(
          (n: Node<NodeDataBase<unknown>>) =>
            !nodes.some((e: Node<NodeDataBase<unknown>>) => e.id === n.id),
        ),
      ])

      // Check if any added node is a start node and update flow type accordingly
      const addedStartNode = nodesToAdd.find((node: Node) => node.type === FlowNodeTypes.start)
      if (addedStartNode) {
        handleStartNodeFlowTypeChange(addedStartNode)
      }
    }

    // Apply node updates
    if (nodesToUpdate.length > 0) {
      const updatedNodesList = currentNodes.map((existingNode) => {
        const updatedNodeDetail = nodesToUpdate.find(
          (updated: Node) => updated.id === existingNode.id,
        )
        if (!updatedNodeDetail) return existingNode
        const merged = { ...existingNode, ...updatedNodeDetail } as GenericNode
        // A user-pinned node name must survive Vibe updates: the agent has no rename
        // tool, so any name difference in its payload is an echo artifact, not intent.
        if (existingNode.data.name_source === 'user') {
          merged.data = {
            ...merged.data,
            name_source: 'user',
            config: { ...merged.data.config, name: existingNode.data.config.name },
          }
        }
        return merged
      })
      updatedNodesList.forEach((node) => {
        updateNodeData(node.id, node.data)
        updateNodeInternals(node.id)
      })

      // Refresh node variables for all updated nodes
      nodesToUpdate.forEach((node: Node) => {
        refreshNodeVars(node.id)
      })

      // Check if any updated node is a start node and update flow type accordingly
      const updatedStartNode = nodesToUpdate.find((node: Node) => node.type === FlowNodeTypes.start)
      if (updatedStartNode) {
        handleStartNodeFlowTypeChange(updatedStartNode)
      }
    }

    // Apply edge additions
    if (edgesToAdd.length > 0) {
      setEdges((edges: GenericEdge[]) => [
        ...edges,
        ...edgesToAdd.filter(
          (e: GenericEdge) => !edges.some((existing: GenericEdge) => existing.id === e.id),
        ),
      ])

      // Refresh node variables for all newly added edges AFTER edges are added
      edgesToAdd.forEach((edge: Edge) => {
        refreshNodeVars(edge.source)
      })
    }

    // Apply edge updates
    if (edgesToUpdate.length > 0) {
      edgesToUpdate.forEach((edge: Edge) => {
        updateEdgeData(edge.id, edge.data)
      })

      // Refresh node variables for all updated edges
      edgesToUpdate.forEach((edge: Edge) => {
        refreshNodeVars(edge.source)
      })
    }

    return { nodesToAdd, nodesToUpdate }
  }

  /**
   * Glows the last newly added node for visual feedback.
   * Viewport (fit/center) is owned by canvas-sync's arrangeAfterApply, so it is not touched here.
   */
  const handleVisualFeedback = (operations: WorkflowUpdateResult) => {
    const { nodesToAdd } = operations

    if (nodesToAdd.length > 0) {
      const lastNewNode = nodesToAdd[nodesToAdd.length - 1]
      const { setGlowingNodeID } = useFlowStore.getState()
      setGlowingNodeID(lastNewNode.id)
    }
  }

  const applyWorkflowUpdate = (workflowJson: string) => {
    try {
      const workflowData = JSON.parse(workflowJson)

      if (!workflowData.nodes || !workflowData.edges) {
        logger.warn('Invalid agentic flow data structure:', workflowData)
        return
      }

      // Step 1: Validate and sanitize the workflow data
      const sanitizedWorkflowData = validateAndSanitizeWorkflowData(workflowData)

      // Step 1.1: Enrich with FE defaults for queue_and_route if needed
      const enrichedWorkflowData = enrichWithDefaults(sanitizedWorkflowData)

      // Get initial state before any operations
      const initialCurrentNodes = getNodes()
      const initialCurrentEdges = getEdges()

      // Step 2: Identify elements to delete
      const elementsToDelete = identifyElementsToDelete(
        enrichedWorkflowData,
        initialCurrentNodes,
        initialCurrentEdges,
      )

      // Step 3: Define the update function
      const executeUpdates = () => {
        // Get the most up-to-date nodes and edges after potential deletions
        const effectiveCurrentNodes = getNodes()
        const effectiveCurrentEdges = getEdges()

        // Step 4: Perform workflow updates
        const updateOperations = performWorkflowUpdates(
          enrichedWorkflowData,
          effectiveCurrentNodes,
          effectiveCurrentEdges,
        )

        // Step 5: Handle visual feedback
        handleVisualFeedback(updateOperations)

        // Step 6: Mark nodes as edited by vibe agent
        const affectedNodeIds = [
          ...updateOperations.nodesToAdd.map((n) => n.id),
          ...updateOperations.nodesToUpdate.map((n) => n.id),
        ]
        if (affectedNodeIds.length > 0) {
          useFlowStore.getState().markVibeAgentChangeForNodes(affectedNodeIds)
        }
      }

      // Execute deletions if any, then proceed with updates
      if (elementsToDelete.nodes.length > 0 || elementsToDelete.edges.length > 0) {
        deleteElements(elementsToDelete)
        setTimeout(executeUpdates, 500) // Delay to ensure deletion completes before updates
      } else {
        executeUpdates() // No deletions, proceed directly with updates
      }
    } catch (e) {
      logger.error('Failed to apply agentic flow update:', e)
    }
  }

  return {
    applyWorkflowUpdate,
  }
}
